import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const isSupabaseConfigured = !!(supabaseUrl && supabaseAnonKey);

export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null;

// Offline, token refreshes just fail and retry (holding the auth lock for
// ~30 s, which blocks every other auth call). Pause them until the connection
// is back; the saved session itself is untouched.
if (supabase && typeof window !== 'undefined') {
  window.addEventListener('offline', () => {
    Promise.resolve(supabase.auth.stopAutoRefresh && supabase.auth.stopAutoRefresh()).catch(() => {});
  });
  window.addEventListener('online', () => {
    Promise.resolve(supabase.auth.startAutoRefresh && supabase.auth.startAutoRefresh()).catch(() => {});
  });
}

// Login codes map to Supabase Auth users via synthetic emails on a subdomain
// that never receives real mail (email confirmation is disabled in Supabase).
const SYNTHETIC_EMAIL_DOMAIN = 'users.sleekapplications.com';

export function toSyntheticEmail(loginCode) {
  return `${loginCode.trim().toLowerCase()}@${SYNTHETIC_EMAIL_DOMAIN}`;
}

function requireSupabase() {
  if (!supabase) {
    throw new Error(
      'Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.'
    );
  }
  return supabase;
}

// ---- Mappers between app (camelCase) and DB (snake_case) ----

function dbToApp(u) {
  return {
    id: u.id,
    authUserId: u.auth_user_id,
    username: u.username || '',
    loginCode: u.login_code,
    isActive: u.is_active,
    userType: u.user_type || 'user',
    createdAt: u.created_at,
  };
}

function appToDb(u) {
  const db = {};
  if (u.username !== undefined) db.username = u.username;
  if (u.isActive !== undefined) db.is_active = u.isActive;
  if (u.userType !== undefined) db.user_type = u.userType;
  return db;
}

// ---- Auth ----

/**
 * Sign in a regular user with login code + password.
 * Returns the user's profile, or null if the credentials are invalid
 * or the account is deactivated.
 */
export async function signIn(loginCode, password) {
  const sb = requireSupabase();
  const { error } = await sb.auth.signInWithPassword({
    email: toSyntheticEmail(loginCode),
    password,
  });
  if (error) {
    if (error.message?.toLowerCase().includes('invalid login credentials')) {
      return null;
    }
    throw error;
  }

  const profile = await getMyProfile();
  if (!profile || !profile.isActive) {
    await sb.auth.signOut();
    return null;
  }
  return profile;
}

/**
 * Sign in an admin with their real email + password.
 * Returns true only if the account carries the admin role
 * (app_metadata.role === 'admin', set in the Supabase dashboard).
 */
export async function signInAdmin(email, password) {
  const sb = requireSupabase();
  const { data, error } = await sb.auth.signInWithPassword({ email, password });
  if (error) return false;
  if (data.user?.app_metadata?.role !== 'admin') {
    await sb.auth.signOut();
    return false;
  }
  return true;
}

export async function signOut() {
  if (!supabase) return;
  await supabase.auth.signOut();
}

export async function getSession() {
  const sb = requireSupabase();
  const { data } = await sb.auth.getSession();
  return data.session;
}

export function isAdminSession(session) {
  return session?.user?.app_metadata?.role === 'admin';
}

const PROFILE_CACHE_KEY = 'sleekHearProfileCache';

function cacheProfile(profile) {
  try {
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
  } catch {
    // storage unavailable — offline restore just won't have a cache
  }
}

/**
 * The last profile fetched while online, used to keep the app usable when it
 * is opened with no connection. Pass `authUserId` to require it to belong to
 * that user. Returns null if there is none or the account was inactive.
 */
export function getCachedProfile(authUserId) {
  try {
    const profile = JSON.parse(localStorage.getItem(PROFILE_CACHE_KEY) || 'null');
    if (!profile || !profile.isActive) return null;
    if (authUserId && profile.authUserId !== authUserId) return null;
    return profile;
  } catch {
    return null;
  }
}

/**
 * Fetch the signed-in user's own profile row (allowed by RLS).
 * Used at login and by the periodic active-status check.
 *
 * Returns null ONLY when there is no session or no profile row. A network or
 * server error is thrown, so callers can tell "offline" from "account removed"
 * and never sign someone out just because the connection dropped. Uses the
 * locally stored session (no extra network round trip to look up the user).
 */
export async function getMyProfile() {
  const sb = requireSupabase();
  const { data: { session }, error: sessionError } = await sb.auth.getSession();
  if (sessionError) throw sessionError;
  if (!session) return null;
  const { data, error } = await sb
    .from('sleek_hear_users')
    .select('*')
    .eq('auth_user_id', session.user.id)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const profile = dbToApp(data);
  cacheProfile(profile);
  return profile;
}

// ---- Admin: user management ----
// Reads and profile-field updates go straight to the table (admin RLS policy).
// Creating/deleting users and resetting passwords need the service-role key,
// so they go through the `admin-users` Edge Function.

export async function getUsers() {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('sleek_hear_users')
    .select('*')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data.map(dbToApp);
}

export async function updateUser(userId, updates) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('sleek_hear_users')
    .update(appToDb(updates))
    .eq('id', userId)
    .select()
    .single();
  if (error) throw error;
  return dbToApp(data);
}

async function invokeAdminUsers(body) {
  const sb = requireSupabase();
  const { data, error } = await sb.functions.invoke('admin-users', { body });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

/**
 * Create a user via the Edge Function. The generated password is returned
 * exactly once and is never stored readable anywhere.
 * Returns { user, password }.
 */
export async function createUser({ username = '' } = {}) {
  const data = await invokeAdminUsers({ action: 'create', username });
  return { user: dbToApp(data.user), password: data.password };
}

export async function deleteUser(userId) {
  await invokeAdminUsers({ action: 'delete', userId });
}

/**
 * Generate a new password for a user. Returns the new password (shown once).
 */
export async function resetPassword(userId) {
  const data = await invokeAdminUsers({ action: 'reset_password', userId });
  return data.password;
}

// ---- Admin: central usage log ----
// Every login adds its own usage rows (see services/usageSync); only an admin
// can read or delete them (row-level security).

const USAGE_PAGE = 1000;
const USAGE_MAX_PAGES = 5; // up to 5,000 newest rows

/**
 * The newest usage rows since `sinceIso`, newest first.
 * `kinds` limits which kinds are fetched ('event' | 'activity' | 'session').
 */
export async function getUsageEvents(sinceIso, kinds = ['event', 'activity', 'session']) {
  const sb = requireSupabase();
  const rows = [];
  for (let page = 0; page < USAGE_MAX_PAGES; page++) {
    const { data, error } = await sb
      .from('usage_events')
      .select('id, user_id, kind, name, session_id, data, created_at')
      .in('kind', kinds)
      .gte('created_at', sinceIso)
      .order('created_at', { ascending: false })
      .range(page * USAGE_PAGE, (page + 1) * USAGE_PAGE - 1);
    if (error) throw error;
    rows.push(...data);
    if (data.length < USAGE_PAGE) break;
  }
  return rows;
}

/** Deletes usage rows of the given kinds (admin only). */
export async function clearUsageEvents(kinds = ['event', 'activity', 'session']) {
  const sb = requireSupabase();
  const { error } = await sb.from('usage_events').delete().in('kind', kinds);
  if (error) throw error;
}
